import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { DollarSign, Clock, Percent } from 'lucide-react';
import { setCached, CACHE_TTL } from '../utils/cacheUtils';
import { getPayTypeColor } from '../utils/payTypeColors';
import { AppContext } from '../App';

const PayTypeBreakdown = ({ API, filters, formatCurrency, formatHours }) => {
  const { premiumPayTypes } = React.useContext(AppContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Create a stable string key for all filters to trigger re-fetches
  const filterKey = JSON.stringify({
    start_date: filters?.start_date || null,
    end_date: filters?.end_date || null,
    location: filters?.location || null,
    department: filters?.department || null,
    manager: filters?.manager || null,
    pay_types: filters?.pay_types || []
  });

  // Build a quick lookup of premium pay types by name (any pay type with PremiumFlag=1)
  const premiumNameSet = React.useMemo(
    () => new Set(premiumPayTypes.map(pt => pt.pay_type)),
    [premiumPayTypes]
  );
  const multiplierByName = React.useMemo(() => {
    const m = {};
    premiumPayTypes.forEach(pt => { m[pt.pay_type] = pt.multiplier ?? 1.0; });
    return m;
  }, [premiumPayTypes]);

  useEffect(() => {
    const fetchData = async () => {
      const params = {
        group_by: 'pay_type',
        start_date: filters?.start_date || undefined,
        end_date: filters?.end_date || undefined,
        location: filters?.location || undefined,
        department: filters?.department || undefined,
        manager: filters?.manager || undefined,
        pay_types: filters?.pay_types && filters.pay_types.length > 0 ? filters.pay_types : undefined
      };

      setLoading(true);
      try {
        const response = await axios.post(`${API}/overtime/analysis`, params);

        // Drive premium classification entirely from AppPayType.PremiumFlag.
        // Records returned by the API have one row per pay type when group_by='pay_type'.
        const premiumRecords = (response.data.records || [])
          .filter(r => premiumNameSet.has(r.dimension))
          .map(r => {
            const multiplier = multiplierByName[r.dimension] ?? 1.0;
            return {
              ...r,
              color: getPayTypeColor(r.dimension),
              multiplier,
              displayName: `${r.dimension} (${multiplier}x)`
            };
          })
          .sort((a, b) => (b.total_pay || 0) - (a.total_pay || 0));

        // Calculate totals for premium types only
        const totalPremiumPay = premiumRecords.reduce((sum, r) => sum + (r.total_pay || 0), 0);
        const totalPremiumHours = premiumRecords.reduce((sum, r) => sum + (r.total_hours || 0), 0);

        const processedData = {
          records: premiumRecords,
          totalPremiumPay,
          totalPremiumHours,
          grandTotalPay: response.data.total_pay,
          premiumPayPercent: response.data.premium_pay_percent
        };

        setData(processedData);
        setCached('paytype_breakdown', params, processedData, CACHE_TTL.LONG);
      } catch (error) {
        console.error('Error fetching pay type data:', error);
        toast.error('Failed to load pay type breakdown');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API, filterKey, premiumNameSet, multiplierByName]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-64 bg-slate-200 rounded"></div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-[400px] bg-slate-100 rounded-lg"></div>
          <div className="h-[400px] bg-slate-100 rounded-lg"></div>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div className="bg-white border border-border rounded-lg shadow-lg p-3">
          <p className="font-semibold text-[#1e3a5f] mb-2">{d.displayName}</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Total Pay:</span>
              <span className="font-medium">{formatCurrency(d.total_pay || 0)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Premium Cost:</span>
              <span className="font-medium text-orange-600">{formatCurrency(d.cost || 0)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Hours:</span>
              <span className="font-medium">{formatHours(d.total_hours || 0)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Employees:</span>
              <span className="font-medium">{d.employee_count}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="kpi-card"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <DollarSign size={20} className="text-blue-600" />
            </div>
            <span className="text-sm text-muted-foreground">Total Pay (All Types)</span>
          </div>
          <div className="text-2xl font-bold text-[#1e3a5f]">{formatCurrency(data?.grandTotalPay || 0)}</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="kpi-card"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <DollarSign size={20} className="text-orange-600" />
            </div>
            <span className="text-sm text-muted-foreground">Premium Pay</span>
          </div>
          <div className="text-2xl font-bold text-orange-600">{formatCurrency(data?.totalPremiumPay || 0)}</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="kpi-card"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Percent size={20} className="text-purple-600" />
            </div>
            <span className="text-sm text-muted-foreground">% Premium of Total</span>
          </div>
          <div className="text-2xl font-bold text-purple-600">{(data?.premiumPayPercent || 0).toFixed(1)}%</div>
        </motion.div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart - Pay by Type */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="kpi-card"
        >
          <h3 className="text-lg font-semibold text-[#1e3a5f] mb-1">Premium Pay by Type</h3>
          <p className="text-sm text-muted-foreground mb-4">Total pay cost for each premium pay type</p>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={data?.records || []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="displayName" width={130} tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="total_pay" name="Total Pay" radius={[0, 4, 4, 0]}>
                {(data?.records || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Pie Chart - Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="kpi-card"
        >
          <h3 className="text-lg font-semibold text-[#1e3a5f] mb-1">Premium Pay Distribution</h3>
          <p className="text-sm text-muted-foreground mb-4">Percentage breakdown by pay type</p>
          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={data?.records || []}
                dataKey="total_pay"
                nameKey="displayName"
                cx="50%"
                cy="50%"
                outerRadius={120}
                label={({ displayName, percent }) => `${displayName}: ${(percent * 100).toFixed(1)}%`}
                labelLine={{ stroke: '#888', strokeWidth: 1 }}
              >
                {(data?.records || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Detail Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="kpi-card"
      >
        <h3 className="text-lg font-semibold text-[#1e3a5f] mb-4">Pay Type Details</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-slate-200">
                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Pay Type</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Multiplier</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Hours</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Total Pay</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Premium Cost</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">% of Premium</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase">Employees</th>
              </tr>
            </thead>
            <tbody>
              {(data?.records || []).map((record, idx) => {
                const pctOfPremium = data?.totalPremiumPay > 0 
                  ? ((record.total_pay || 0) / data.totalPremiumPay * 100) 
                  : 0;
                return (
                  <tr key={record.dimension || `pt-${idx}`} className="border-b border-border hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: record.color }}></div>
                        <span className="font-medium">{record.dimension}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums">{record.multiplier}x</td>
                    <td className="py-3 px-4 text-right tabular-nums">{formatHours(record.total_hours || 0)}</td>
                    <td className="py-3 px-4 text-right tabular-nums font-medium">{formatCurrency(record.total_pay || 0)}</td>
                    <td className="py-3 px-4 text-right tabular-nums text-orange-600 font-medium">{formatCurrency(record.cost || 0)}</td>
                    <td className="py-3 px-4 text-right">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        {pctOfPremium.toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums">{record.employee_count}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default PayTypeBreakdown;
